import React, { useRef, useEffect } from 'react';
import ScrollAnimation from './ScrollAnimation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Spec icons as SVG components
const SpecIcons = {
  android: () => (
    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67c-.19-.28-.54-.37-.83-.22-.3.16-.42.54-.26.85l1.84 3.18C4.5 11.1 3 13.9 3 17c0 2.5 2 4.5 4.5 4.5h13c2.5 0 4.5-2 4.5-4.5 0-3.1-1.5-5.9-3.4-7.52zM6.75 14c-.69 0-1.25-.56-1.25-1.25S6.06 11.5 6.75 11.5 8 12.06 8 12.75 7.44 14 6.75 14zm10.5 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
    </svg>
  ),
  chip: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
    </svg>
  ),
  printer: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
  ),
  document: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  display: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  battery: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  batteryAlt: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
    </svg>
  ),
  // Battery (rectangle with terminal)
  batteryRect: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="2" y="7" width="16" height="10" rx="2" ry="2" strokeWidth={2} />
      <line x1="22" y1="11" x2="22" y2="13" strokeWidth={2} strokeLinecap="round" />
    </svg>
  ),
  camera: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4M14 13v4" />
    </svg>
  ),
  sim: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  ruler: () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  ),
};

const DEFAULT_SPECS = [
  { icon: 'android', text: 'Android 13' },
  { icon: 'chip', text: '3GB RAM + 32GB ROM' },
  { icon: 'printer', text: 'Max 90mm\'s speed printing' },
  { icon: 'document', text: 'عرض الورق 58mm' },
  { icon: 'display', text: '5.5" HD+, 1440×720\nCapacitive multi-touch' },
  { icon: 'batteryRect', text: '7.7V 3000mAh (= 3.85V 6000mAh)\nبطارية ليثيوم قابلة للإزالة' },
  { icon: 'camera', text: '5MP Mega-Pixel Camera Scan' },
  { icon: 'sim', text: '1× Nano SIM\n1× Micro SD' },
  { icon: 'ruler', text: '219×80×17.9mm (Size)' },
];

const PRODUCT_3_SPECS = [
  { icon: 'android', text: 'Android 14.0' },
  { icon: 'chip', text: 'T606 8-Core 2.0GHz (2×A73 + 6×A53)' },
  { icon: 'document', text: '3GB (4GB opt.) RAM / 32GB (64GB opt.) Storage' },
  { icon: 'display', text: '1600×720 IPS' },
  { icon: 'ruler', text: '247.8 × 104(82.5) × 68.9(19) mm, 400g' },
  { icon: 'batteryRect', text: '3.8V 6400mAh (or 7.6V 3200mAh)' },
  { icon: 'printer', text: '80mm built-in thermal printer' },
  { icon: 'camera', text: '2.0 MP (Front) / 5.0 MP (Rear)' },
  { icon: 'sim', text: 'SIM × 2' },
];

const PRODUCT_4_SPECS = [
  { icon: 'android', text: 'Android 14' },
  { icon: 'chip', text: 'ARM 8803 Cortex A8' },
  { icon: 'document', text: '32 GB SSD' },
  { icon: 'display', text: '6.75" display, 1920 px' },
  { icon: 'ruler', text: 'Bar form factor' },
  { icon: 'printer', text: 'Built-in thermal printer, NFC' },
];

// Product Gallery Carousel Component with Swiper
const ProductCarousel = ({ product,mediaItems ,isHighlighted, }) => {
  const videoRefs = useRef([]);
  


  const handleSlideChange = (swiper) => {
    // Pause all videos
    videoRefs.current.forEach((video) => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    // Play video if entering video slide
    if (swiper.activeIndex === 1 && videoRefs.current[1]) {
      setTimeout(() => {
        const video = videoRefs.current[1];
        if (video) {
          video.play().catch(() => {
            // Auto-play might be blocked
          });
        }
      }, 300);
    }
  };

  return (
    <div className="relative w-full h-full group">
      <Swiper
        modules={[Navigation, Pagination]}
        navigation={{
          nextEl: `.swiper-button-next-${product.id}`,
          prevEl: `.swiper-button-prev-${product.id}`,
        }}
        pagination={{
          clickable: true,
          el: `.swiper-pagination-${product.id}`,
        }}
        onSlideChange={handleSlideChange}
        className="w-full h-full"
        spaceBetween={0}
        slidesPerView={1}
        loop={false}
        allowTouchMove={true}
      >
        {mediaItems.map((item, index) => (
          <SwiperSlide key={index} className="!flex items-center justify-center">
            {item.type === 'image' ? (
              <img
                src={item.src}
                alt={item.alt}
                className="w-full h-full object-contain"
              />
            ) : (
              <video
                ref={(el) => {
                  videoRefs.current[index] = el;
                }}
                className="w-full h-full object-contain"
                loop
                muted
                playsInline
                controls={false}
                preload="metadata"
                onLoadedData={(e) => {
                  // Auto-play when video is loaded and it's the active slide
                  if (index === 1) {
                    setTimeout(() => {
                      e.target.play().catch(() => {});
                    }, 500);
                  }
                }}
              >
                <source src={item.src} type="video/quicktime" />
                <source src={item.src} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Navigation Arrows — positions swapped for RTL: left = next, right = prev */}
      <button
        className={`swiper-button-next-${product.id} absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-700 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 cursor-pointer`}
        aria-label="التالي"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        className={`swiper-button-prev-${product.id} absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-700 rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 cursor-pointer`}
        aria-label="السابق"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Custom Pagination Dots */}
      <div className={`swiper-pagination swiper-pagination-${product.id} !absolute !bottom-4 !left-1/2 !-translate-x-1/2 !z-20`} />
    </div>
  );
};

const ProductShowcase = () => {
  const products = [
    {
      id: 1,
      type: 'standard',
      badge: 'الجهاز فقط',
      title: 'جهاز الكاشير Casher V5',
      description: 'جهاز نقاط بيع متكامل بشاشة واضحة وطابعة فواتير مدمجة. الجهاز فقط بدون برمجيات.',
      image: '/device.png',
      specs: DEFAULT_SPECS,
      mediaItems: [
        { type: 'image', src: '/images/product_2.png', alt: "جهاز الكاشير Casher V5" },
        { type: 'video', src: '/videos/product_2_video.MOV', alt: "جهاز الكاشير Casher V5" }
      ],
      features: ['الجهاز فقط', 'ضمان المصنع', 'دليل الاستخدام'],
      productUrl: 'https://salla.sa/cash-tec/wAYONYR',
      ctaLabel: 'شراء الآن'
    },
    {
      id: 2,
      type: 'withSoftware',
      badge: 'مع البرمجيات',
      title: 'Casher V5 مع البرمجيات',
      subtitle: 'جهاز + برمجيات نقاط البيع',
      description: 'جهاز نقاط بيع مع حزمة برمجيات كاملة: تركيب، تدريب، دعم فني وتحديثات مستمرة.',
      image: '/device.png',
      mediaItems: [
        { type: 'image', src: '/images/product_image.png', alt: "جهاز الكاشير Casher V5" },
        { type: 'video', src: '/videos/product_video.MOV', alt: "جهاز الكاشير Casher V5" }
      ],
      specs: DEFAULT_SPECS,
      features: ['الجهاز + برمجيات نقاط البيع', 'تركيب وتدريب', 'دعم فني وتحديثات', 'ضمان شامل'],
      ctaLabel: 'شراء الآن',
      productUrl: 'https://salla.sa/cash-tec/oZzBpbQ',
      isHighlighted: true
    },
    {
      id: 3,
      type: 'standard',
      badge: 'جهاز محمول',
      title: 'Cash tek v6',
      description: 'جهاز نقاط بيع محمول بشاشة لمس وطابعة حرارية مدمجة، دعم NFC وبطاقات الدفع، شبكة 4G وواي فاي.',
      image: '/device.png',
      mediaItems: [
        { type: 'image', src: '/images/product_3_1.jpg', alt: 'جهاز نقاط بيع محمول' },
        { type: 'image', src: '/images/product_3_2.jpg', alt: 'جهاز نقاط بيع محمول' }
      ],
      specs: PRODUCT_3_SPECS,
      features: ['طابعة حرارية 80mm', 'NFC وبطاقات مغناطيسية', 'ماسح باركود اختياري', 'GPS'],
      productUrl: 'https://salla.sa/cash-tec/zoxeGpo',
      ctaLabel: 'شراء الآن'
    },
    {
      id: 4,
      type: 'standard',
      badge: 'SUNMI',
      title: 'SUNMI V3',
      description: 'جهاز نقاط بيع محمول من صنمي، شاشة 6.75 بوصة وطابعة حرارية مدمجة وقارئ دفع لمسي (NFC). تصميم Bar احترافي.',
      image: '/device.png',
      mediaItems: [
        { type: 'image', src: '/images/product_4_1.png', alt: 'SUNMI V3' },
        { type: 'video', src: '/videos/Video Project_4.mov', alt: 'SUNMI V3' },
        { type: 'image', src: '/images/product_4_3.png', alt: 'SUNMI V3' }
      ],
      specs: PRODUCT_4_SPECS,
      features: ['طراز T5F1A', 'شاشة 6.75 بوصة', 'NFC', 'طابعة حرارية مدمجة'],
      productUrl: 'https://salla.sa/cash-tec/zoxeVqE',
      ctaLabel: 'شراء الآن'
    }
  ];

  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden" id="products">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <span className="text-primary-500 font-bold tracking-wider uppercase text-sm">متجر الأجهزة</span>
          <h2 className="text-4xl font-bold text-slate-900 mt-2 mb-4">
            أجهزة نقاط بيع احترافية
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            اختر من تشكيلة أجهزتنا المعتمدة لضمان أفضل أداء واستقرار لنظامك
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {products.map((product, index) => (
            <ScrollAnimation key={product.id} variant="fade-up" delay={index * 150}>
                <div 
                  className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group flex flex-col h-full relative ${
                    product.isHighlighted ? 'border-2 border-primary-500 ring-2 ring-primary-500/20' : 'border border-slate-100'
                  }`}
                >

                  {/* Product Gallery Carousel */}
                  <div className={`relative bg-transparent overflow-hidden flex items-center justify-center  ${product.isHighlighted ? 'h-80 ' : 'h-80'}`}>
                    <ProductCarousel product={product} isHighlighted={product.isHighlighted} mediaItems={product.mediaItems} />
                    

                  </div>

                  {/* Content */}
                  <div className="p-8 flex-1 flex flex-col">

                    <h3 className={`font-bold text-slate-900 mb-2 group-hover:text-primary-500 transition-colors ${product.subtitle ? 'text-lg' : 'text-xl'}`}>
                      {product.title}
                    </h3>
                    <p className="text-slate-500 text-sm mb-4">
                      {product.description}
                    </p>

                    {product.features && product.features.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.features.map((f, i) => (
                          <span key={i} className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">
                            {f}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    <div className="space-y-3 mb-6 flex-1 min-h-0">
                      {product.specs.map((spec, index) => {
                        const IconComponent = SpecIcons[spec.icon];
                        return (
                          <div key={index} className="flex items-start gap-3 text-sm text-slate-600">
                            <span className="text-primary-500 mt-0.5">
                              {IconComponent ? <IconComponent /> : <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />}
                            </span>
                            <span className="whitespace-pre-line">{spec.text}</span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-auto">
                      <a 
                        href={`${product.productUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full py-3.5 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 ${product.isHighlighted ? 'bg-primary-500 text-white hover:bg-primary-600 group-hover:shadow-lg group-hover:shadow-primary-500/30' : 'bg-slate-900 text-white hover:bg-primary-500 group-hover:shadow-lg group-hover:shadow-primary-500/20'}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        {product.ctaLabel}
                      </a>
                    </div>
                  </div>
                </div>
            </ScrollAnimation>
          ))}
        </div>

        <div className="mt-16 text-center">
            <p className="text-slate-500 mb-4">هل تحتاج إلى مساعدة في اختيار الأجهزة المناسبة؟</p>
            <a href="https://wa.me/966557990109" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary-500 font-bold hover:text-primary-600 underline">
                تواصل مع فريق المبيعات
            </a>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
